import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, logUsage } from "@/lib/api";
import { translate, personaLabel } from "@/lib/i18n";

const Ctx = createContext(null);
const ls = (k, d) => localStorage.getItem(k) ?? d;

async function loadCatalog() {
  const cached = sessionStorage.getItem("q1_catalog");
  const { data: v } = await api.get("/catalog/version");
  if (cached) {
    const c = JSON.parse(cached);
    if (c.version === v.version) return c;
  }
  const { data } = await api.get("/catalog");
  try { sessionStorage.setItem("q1_catalog", JSON.stringify(data)); } catch (e) { /* quota */ }
  return data;
}

export function AppProvider({ children }) {
  const [lang, setLangState] = useState(ls("q1_lang", "en"));
  const [voiceLang, setVoiceLangState] = useState(ls("q1_voice_lang", "en"));
  const [theme, setThemeState] = useState(ls("q1_theme", "light"));
  const [consent, setConsentState] = useState(ls("q1_consent", "true") === "true");
  const [demoUser, setDemoUserState] = useState(ls("q1_demo_user", "khalid@demo.qa"));
  const [catalog, setCatalog] = useState(null);
  const [me, setMe] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);

  useEffect(() => { localStorage.setItem("q1_demo_user", demoUser); }, [demoUser]);
  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);
  useEffect(() => { document.documentElement.classList.toggle("dark", theme === "dark"); }, [theme]);
  useEffect(() => { loadCatalog().then(setCatalog).catch(() => {}); }, []);

  const refreshMe = useCallback(() => api.get("/me/summary").then((r) => setMe(r.data)).catch(() => setMe(null)), []);
  useEffect(() => { refreshMe(); }, [demoUser, refreshMe]);

  const setLang = (l) => {
    setLangState(l); localStorage.setItem("q1_lang", l);
    if (l === "ar" || l === "en") { setVoiceLangState(l); localStorage.setItem("q1_voice_lang", l); }
    logUsage({ type: "language", lang: l, value: "ui" });
  };
  const setVoiceLang = (l) => { setVoiceLangState(l); localStorage.setItem("q1_voice_lang", l); logUsage({ type: "language", lang: l, value: "voice" }); };
  const setTheme = (t) => { setThemeState(t); localStorage.setItem("q1_theme", t); };
  const setConsent = (c) => { setConsentState(c); localStorage.setItem("q1_consent", String(c)); };
  const setDemoUser = (e) => { localStorage.setItem("q1_demo_user", e); setDemoUserState(e); };

  const t = useCallback((k) => translate(lang, k), [lang]);
  const svcName = useCallback((s) => (s ? (lang === "ar" ? s.name_ar : s.name_en) : ""), [lang]);
  const secName = useCallback((s) => (s ? (lang === "ar" ? s.sector_ar : s.sector_en) : ""), [lang]);
  const pLabel = useCallback((p) => personaLabel(lang, p), [lang]);
  const reloadCatalog = () => { sessionStorage.removeItem("q1_catalog"); loadCatalog().then(setCatalog); };

  const value = useMemo(() => ({
    lang, setLang, voiceLang, setVoiceLang, theme, setTheme, consent, setConsent, demoUser, setDemoUser,
    catalog, reloadCatalog, me, refreshMe, t, svcName, secName, pLabel, assistantOpen, setAssistantOpen,
  }), [lang, voiceLang, theme, consent, demoUser, catalog, me, t, svcName, secName, pLabel, assistantOpen, refreshMe]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useApp = () => useContext(Ctx);
